import { useEffect, useState } from 'react';
import { Badge, Card, ErrorMessage, Loading, ModuleHeader, useAsync, useToast } from '../../components/ui';
import { IconTrash } from '../../components/icons';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';

interface Row {
  key: string;
  label: string;
  description: string;
  action: 'delete' | 'anonymize' | 'archive-delete';
  months: number;
  defaultMonths: number;
  due: number;
  cutoff: string;
}

const ACTION: Record<Row['action'], { verb: string; button: string; warn: string }> = {
  delete: { verb: 'Löschen', button: 'Löschen …', warn: 'werden unwiderruflich gelöscht' },
  anonymize: {
    verb: 'Unkenntlich machen',
    button: 'Unkenntlich machen …',
    warn: 'werden unkenntlich gemacht (die Datensätze selbst bleiben)',
  },
  'archive-delete': {
    verb: 'Archivieren und löschen',
    button: 'Archivieren und löschen …',
    warn: 'werden zuerst in ein Archiv (ZIP) im Sicherungsverzeichnis geschrieben und dann gelöscht',
  },
};

/** Frist in Monaten, direkt änderbar; gespeichert beim Verlassen des Feldes oder mit Enter. */
function MonthsField({ row, onSave }: { row: Row; onSave: (months: number) => void }) {
  const [text, setText] = useState(String(row.months));
  useEffect(() => setText(String(row.months)), [row.months]);

  const commit = () => {
    const n = Number(text);
    if (text.trim() !== '' && Number.isInteger(n) && n >= 1 && n <= 120) {
      if (n !== row.months) onSave(n);
    } else {
      setText(String(row.months));
    }
  };

  return (
    <span className="row" style={{ gap: 8, alignItems: 'center' }}>
      <input
        type="number"
        min={1}
        max={120}
        value={text}
        style={{ width: 76 }}
        aria-label={`Frist in Monaten: ${row.label}`}
        onChange={(event) => setText(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => event.key === 'Enter' && (event.target as HTMLInputElement).blur()}
      />
      <span className="small subtle">Monate</span>
    </span>
  );
}

/**
 * Löschfristen: Was ist fällig, und was soll bereinigt werden?
 *
 * Es gibt keine Automatik. Bei Fälligkeit erscheint ein Hinweis in der Glocke
 * (Administratoren, Superadmins); gelöscht wird erst hier, auf Knopfdruck, und
 * jede Bereinigung steht im Audit-Log.
 */
export function RetentionPage() {
  const { notify } = useToast();
  const data = useAsync(() => api.get('/api/retention'), []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  if (data.loading) return <Loading />;
  if (data.error || !data.data) return <ErrorMessage error={data.error ?? new Error('Nicht geladen.')} />;

  const rows: Row[] = data.data.categories;
  const notice = data.data.lastNotice as { at: string; due: { label: string; due: number }[] } | null;
  const dueRows = rows.filter((row) => row.due > 0);

  const run = async (selected: Row[]) => {
    const lines = selected.map((row) => `• ${row.label}: ${row.due} – ${ACTION[row.action].warn}`).join('\n');
    if (!window.confirm(`Diese Daten sind fällig:\n\n${lines}\n\nJetzt ausführen? Das lässt sich nicht rückgängig machen.`)) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.post('/api/retention/run', { keys: selected.map((row) => row.key) });
      const failed = result.results.filter((entry: any) => entry.error);
      const total = result.results.reduce((sum: number, entry: any) => sum + entry.affected, 0);
      const files = result.results.filter((entry: any) => entry.file).map((entry: any) => entry.file);
      notify(
        failed.length
          ? `Teilweise ausgeführt: ${failed.map((entry: any) => `${entry.label}: ${entry.error}`).join('; ')}`
          : `${total} Datensätze bereinigt${files.length ? ` – Archiv: ${files.join(', ')}` : ''}.`,
        failed.length ? 'error' : 'success',
      );
      data.reload();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  const saveMonths = async (row: Row, months: number) => {
    setError(null);
    try {
      await api.put(`/api/retention/settings/${row.key}`, { months });
      data.reload();
      notify(`Frist „${row.label}“: ${months} Monate.`, 'success');
    } catch (caught) {
      setError(caught);
    }
  };

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconTrash width={22} height={22} />}
        title="Löschfristen"
        subtitle="Aufbewahrungsfristen je Datenart. Bei Fälligkeit erscheint ein Hinweis; gelöscht wird nur hier und nur auf Ihre Auslösung."
        pill="Datenschutz"
        tone="info"
      />

      <div className="note-box tone-info">
        <span>
          Es läuft <strong>keine automatische Löschung</strong>. Die Anwendung prüft regelmäßig und weist
          Administratoren und Superadmins in der Glocke hin; der Hinweis steht zugleich im Audit-Log.{' '}
          {notice ? (
            <>Letzter Hinweis: {formatDateTime(notice.at)}.</>
          ) : (
            <>Aktuell liegt kein offener Hinweis vor.</>
          )}{' '}
          Sicherungen (<code>BACKUP_DIR</code>) enthalten gelöschte Daten weiter, bis sie selbst ausgemustert werden.
        </span>
      </div>

      {error ? <ErrorMessage error={error} /> : null}

      <Card
        title="Datenarten"
        subtitle={dueRows.length ? `${dueRows.length} mit fälligen Daten` : 'Nichts fällig'}
        actions={
          <button className="btn danger" disabled={busy || dueRows.length === 0} onClick={() => run(dueRows)}>
            Alle fälligen bereinigen …
          </button>
        }
        noBody
      >
        <table className="table">
          <thead>
            <tr>
              <th>Datenart</th>
              <th style={{ width: 170 }}>Frist</th>
              <th style={{ width: 120 }}>Fällig</th>
              <th style={{ width: 230 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td>
                  <strong>{row.label}</strong>
                  <div className="small subtle">{row.description}</div>
                </td>
                <td>
                  <MonthsField row={row} onSave={(months) => saveMonths(row, months)} />
                  {row.months !== row.defaultMonths && <div className="small subtle">Vorgabe: {row.defaultMonths}</div>}
                </td>
                <td>
                  {row.due > 0 ? (
                    <Badge variant="warning" dot>
                      {row.due} fällig
                    </Badge>
                  ) : (
                    <Badge variant="success">0</Badge>
                  )}
                  <div className="small subtle">älter als {row.cutoff.slice(0, 10)}</div>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn sm" disabled={busy || row.due === 0} onClick={() => run([row])}>
                    {ACTION[row.action].button}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
